import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { GET_BASE_URL } from "../constants/apiEndpoints";
import {
  buildLoungeAuthHeaders,
  formatLoungeBearerAuthorization,
  isRealLoungeAccessToken,
} from "./loungeAuthHeader";
import {
  clearSessionExpired,
  isAuthFailurePayload,
  isSessionExpired,
  markSessionExpired,
  noteAuthFailureFromError,
  noteAuthFailureFromPayload,
  restoreLoungeSession,
} from "./loungeSession";

export { buildLoungeAuthHeaders, isRealLoungeAccessToken };

/** Documented MWBACKEND refresh path. Header must be Bearer <current token>. */
export const LOUNGE_AUTH_REFRESH_PATH = "/api/v1/auth/refresh";

export type LoungeRefreshResult =
  | { kind: "ok"; token: string }
  | { kind: "unauthorized" }
  | { kind: "transient" }
  | { kind: "aborted" };

type RefreshApplyDecision = "apply" | "skip" | "abort";

type LoungeRetryConfig = AxiosRequestConfig & {
  _loungeAuthRetried?: boolean;
};

const installedInstances = new WeakSet<AxiosInstance>();

let inFlightRefresh: Promise<LoungeRefreshResult> | null = null;
let lastUnauthorizedToken: string | null = null;
let lastLoginCopyAfterRefreshToken: string | null = null;

const isRealAccessToken = isRealLoungeAccessToken;

const readHeader = (
  headers: unknown,
  name: string
): string | undefined => {
  if (!headers) {
    return undefined;
  }
  const rec = headers as {
    get?: (key: string) => unknown;
    [key: string]: unknown;
  };
  if (typeof rec.get === "function") {
    const value = rec.get(name) ?? rec.get(name.toLowerCase());
    return typeof value === "string" ? value : undefined;
  }
  const value =
    rec[name] ?? rec[name.toLowerCase()] ?? rec.Authorization ?? rec.authorization;
  return typeof value === "string" ? value : undefined;
};

const writeAuthorization = (headers: unknown, value: string) => {
  if (!headers) {
    return { Authorization: value };
  }
  const rec = headers as {
    set?: (key: string, next: string) => void;
    Authorization?: string;
    authorization?: string;
  };
  if (typeof rec.set === "function") {
    rec.set("Authorization", value);
    return headers;
  }
  rec.Authorization = value;
  return headers;
};

export const isPostRefreshLoginCopyToken = (
  token?: string | null
): boolean => Boolean(token) && token === lastLoginCopyAfterRefreshToken;

const rememberLoginCopyAfterRefresh = (token: string | null | undefined) => {
  lastLoginCopyAfterRefreshToken = isRealAccessToken(token) ? token : null;
};

const clearLoginCopyAfterRefresh = () => {
  lastLoginCopyAfterRefreshToken = null;
};

export const readStoredAccessToken = (): string | null => {
  try {
    const token = localStorage.getItem("token");
    return isRealAccessToken(token) ? token : null;
  } catch {
    return null;
  }
};

export const readStoredUserId = (): string | null => {
  try {
    return localStorage.getItem("user_id");
  } catch {
    return null;
  }
};

const decideRefreshApply = (
  expectedToken: string,
  expectedUserId: string | null
): RefreshApplyDecision => {
  const current = readStoredAccessToken();
  if (!current) {
    return "abort";
  }
  const userId = readStoredUserId();
  if ((expectedUserId ?? "") !== (userId ?? "")) {
    return "abort";
  }
  if (current === expectedToken) {
    return "apply";
  }
  return "skip";
};

export const requestSentBearer = (
  config?: { headers?: unknown } | null
): boolean => {
  const auth = readHeader(config?.headers, "Authorization");
  if (!auth) {
    return false;
  }
  const match = auth.trim().match(/^bearer\s+(\S+)/i);
  if (!match) {
    return false;
  }
  return isRealAccessToken(match[1]);
};

export const isAuthRefreshUrl = (url?: string): boolean =>
  typeof url === "string" && url.includes(LOUNGE_AUTH_REFRESH_PATH);

export const isIdempotentMethod = (method?: string) => {
  const normalized = (method || "GET").toUpperCase();
  switch (normalized) {
    case "GET":
    case "HEAD":
    case "OPTIONS":
      return true;
    case "POST":
    case "PUT":
    case "PATCH":
    case "DELETE":
    case "CONNECT":
    case "TRACE":
      return false;
    default:
      return false;
  }
};

const MUTATING_GET_PATHS = ["/api/v1/sendCredit"] as const;

export const isSafeToReplayUrl = (url?: string) => {
  if (!url) {
    return true;
  }
  return !MUTATING_GET_PATHS.some((path) => url.includes(path));
};

export const canReplayRequestBody = (body: unknown): boolean => {
  if (body == null) {
    return true;
  }
  if (typeof body === "string") {
    return true;
  }
  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
    return true;
  }
  if (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) {
    return true;
  }
  if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(body)) {
    return true;
  }
  return false;
};

export const isHttp401 = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 401;

export const makeTransientRefreshError = (
  _cause?: unknown
): Error & { isTransientRefreshFailure: true } => {
  const error = new Error(
    "Session refresh temporarily unavailable"
  ) as Error & {
    isTransientRefreshFailure: true;
  };
  error.isTransientRefreshFailure = true;
  return error;
};

const loungeRefreshUrl = () => GET_BASE_URL + LOUNGE_AUTH_REFRESH_PATH;

const applyRefreshedAccessToken = (token: string) => {
  localStorage.setItem("token", token);
  restoreLoungeSession();
};

type NetworkRefreshResult = Exclude<LoungeRefreshResult, { kind: "aborted" }>;

const requestRefresh = async (
  currentToken: string
): Promise<NetworkRefreshResult> => {
  let response: Response;
  try {
    response = await fetch(loungeRefreshUrl(), {
      method: "POST",
      headers: {
        ...buildLoungeAuthHeaders(currentToken),
        Accept: "application/json",
      },
    });
  } catch {
    return { kind: "transient" };
  }

  if (response.status === 401) {
    return { kind: "unauthorized" };
  }
  if (response.status === 429 || response.status >= 500 || !response.ok) {
    return { kind: "transient" };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: "transient" };
  }

  const token = (body as { access_token?: unknown })?.access_token;
  if (typeof token !== "string" || !isRealAccessToken(token)) {
    return { kind: "transient" };
  }
  return { kind: "ok", token };
};

export const refreshLoungeAccessToken = (): Promise<LoungeRefreshResult> => {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  const current = readStoredAccessToken();
  if (!current) {
    return Promise.resolve({ kind: "unauthorized" });
  }
  if (current === lastUnauthorizedToken) {
    return Promise.resolve({ kind: "unauthorized" });
  }
  const expectedUserId = readStoredUserId();

  const run = (async (): Promise<LoungeRefreshResult> => {
    try {
      const result = await requestRefresh(current);
      switch (result.kind) {
        case "ok": {
          const decision = decideRefreshApply(current, expectedUserId);
          switch (decision) {
            case "apply":
              lastUnauthorizedToken = null;
              applyRefreshedAccessToken(result.token);
              return result;
            case "skip": {
              const live = readStoredAccessToken();
              if (!live) {
                return { kind: "aborted" };
              }
              lastUnauthorizedToken = null;
              return { kind: "ok", token: live };
            }
            case "abort":
              return { kind: "aborted" };
            default: {
              const _exhaustive: never = decision;
              return _exhaustive;
            }
          }
        }
        case "unauthorized":
          lastUnauthorizedToken = current;
          clearLoginCopyAfterRefresh();
          markSessionExpired("refresh");
          return result;
        case "transient":
          return result;
        default: {
          const _exhaustive: never = result;
          return _exhaustive;
        }
      }
    } catch {
      return { kind: "transient" };
    }
  })();

  inFlightRefresh = run.finally(() => {
    if (inFlightRefresh === run) {
      inFlightRefresh = null;
    }
  });
  return inFlightRefresh;
};

const retryOnceAfterRefresh = async <T>(
  error: unknown,
  retry: (token: string) => Promise<T>
): Promise<T> => {
  const result = await refreshLoungeAccessToken();
  switch (result.kind) {
    case "ok":
      return retry(result.token);
    case "unauthorized":
    case "aborted":
      throw error;
    case "transient":
      throw makeTransientRefreshError(error);
    default: {
      const _exhaustive: never = result;
      throw _exhaustive;
    }
  }
};

export const loungeAuthorizedGet = async <T = any>(
  url: string,
  token: string | null | undefined,
  extraConfig?: AxiosRequestConfig
): Promise<T> => {
  const extraHeaders =
    (extraConfig?.headers as Record<string, string> | undefined) ?? {};
  const headers = {
    ...extraHeaders,
    ...buildLoungeAuthHeaders(token),
  };
  const sentBearer = Boolean(headers.Authorization);
  const alreadyRetried = Boolean(
    (extraConfig as LoungeRetryConfig | undefined)?._loungeAuthRetried
  );

  const getWithToken = async (
    requestToken: string | null | undefined,
    retried: boolean
  ) => {
    const requestHeaders = {
      ...extraHeaders,
      ...buildLoungeAuthHeaders(requestToken),
    };
    const response = await axios.get<T>(url, {
      ...extraConfig,
      headers: requestHeaders,
      ...(retried ? { _loungeAuthRetried: true } : {}),
    } as LoungeRetryConfig);
    return response.data;
  };

  let data: T;
  try {
    data = await getWithToken(token, alreadyRetried);
  } catch (error) {
    const requestAlreadyRetried = Boolean(
      (error as { config?: LoungeRetryConfig })?.config?._loungeAuthRetried
    );
    if (
      !sentBearer ||
      !isHttp401(error) ||
      isAuthRefreshUrl(url) ||
      alreadyRetried ||
      requestAlreadyRetried
    ) {
      throw error;
    }
    return retryOnceAfterRefresh(error, (nextToken) =>
      getWithToken(nextToken, true)
    );
  }

  // Store-like endpoints return 200 with login credit copy instead of 401.
  if (sentBearer && !alreadyRetried && isAuthFailurePayload(data)) {
    const live = readStoredAccessToken();
    if (isPostRefreshLoginCopyToken(live)) {
      return data;
    }
    const softError = {
      response: { status: 401, data },
      isAuthFailure: true,
    };
    return retryOnceAfterRefresh(softError, async (nextToken) => {
      const retried = await getWithToken(nextToken, true);
      if (isAuthFailurePayload(retried)) {
        // Refresh worked. Leftover login-copy is empty-data or a backend
        // quirk — do not refresh again and do not expire the session.
        rememberLoginCopyAfterRefresh(nextToken);
      } else {
        clearLoginCopyAfterRefresh();
      }
      return retried;
    });
  }

  if (!isAuthFailurePayload(data)) {
    clearLoginCopyAfterRefresh();
  }

  return data;
};

const unwrapAuthorizedBody = <T>(
  body: unknown,
  sanitizeLoginCopy: boolean
): T => {
  const record = body && typeof body === "object" ? (body as { data?: T }) : null;
  const data = (record && "data" in record ? record.data : body) as T;
  if (
    sanitizeLoginCopy &&
    isAuthFailurePayload(body) &&
    data &&
    typeof data === "object" &&
    !Array.isArray(data)
  ) {
    const storeLike = data as { credit_balance?: unknown };
    if (typeof storeLike.credit_balance === "string") {
      return {
        ...storeLike,
        credit_balance: null,
      } as unknown as T;
    }
  }
  return data;
};

export const readLoungeAuthorizedData = async <T = any>(
  url: string,
  reason: string
): Promise<T> => {
  const token = localStorage.getItem("token");
  try {
    const body = await loungeAuthorizedGet(url, token);
    if (
      isAuthFailurePayload(body) &&
      isPostRefreshLoginCopyToken(readStoredAccessToken())
    ) {
      clearSessionExpired();
      return unwrapAuthorizedBody<T>(body, true);
    }
    if (noteAuthFailureFromPayload(body, reason)) {
      const error = new Error("Sign in required");
      (error as { isAuthFailure?: boolean }).isAuthFailure = true;
      throw error;
    }
    clearSessionExpired();
    return unwrapAuthorizedBody<T>(body, false);
  } catch (error) {
    noteAuthFailureFromError(error, reason);
    throw error;
  }
};

export const recoverLoungeAxiosError = async (
  error: unknown,
  instance: AxiosInstance = axios
) => {
  const config = (error as { config?: LoungeRetryConfig })?.config;
  if (
    !config ||
    config._loungeAuthRetried ||
    isAuthRefreshUrl(config.url) ||
    !isHttp401(error) ||
    !requestSentBearer(config)
  ) {
    return Promise.reject(error);
  }
  if (isSessionExpired() && readStoredAccessToken() === lastUnauthorizedToken) {
    return Promise.reject(error);
  }
  if (
    !isIdempotentMethod(config.method) ||
    !canReplayRequestBody(config.data) ||
    !isSafeToReplayUrl(config.url)
  ) {
    await refreshLoungeAccessToken();
    return Promise.reject(error);
  }

  return retryOnceAfterRefresh(error, async (nextToken) => {
    config._loungeAuthRetried = true;
    config.headers = writeAuthorization(
      config.headers,
      formatLoungeBearerAuthorization(nextToken)
    ) as LoungeRetryConfig["headers"];
    return instance.request(config);
  });
};

export const installLoungeAuthRefresh = (
  instance: AxiosInstance = axios
) => {
  if (installedInstances.has(instance)) {
    return;
  }
  if (typeof instance?.interceptors?.response?.use !== "function") {
    return;
  }
  instance.interceptors.response.use(
    (response) => response,
    (error) => recoverLoungeAxiosError(error, instance)
  );
  installedInstances.add(instance);
};

type LoungeFetchInit = RequestInit & {
  _loungeAuthRetried?: boolean;
};

const resolveFetchUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") {
    return input;
  }
  if (typeof URL !== "undefined" && input instanceof URL) {
    return input.toString();
  }
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.url;
  }
  return String(input);
};

const isLoungeBackendUrl = (url: string) =>
  url.startsWith(GET_BASE_URL) && !isAuthRefreshUrl(url);

const resolveFetchMethod = (
  input: RequestInfo | URL,
  init?: RequestInit
): string => {
  if (init?.method) {
    return init.method;
  }
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.method;
  }
  return "GET";
};

let fetchPatched = false;
let nativeFetch: typeof fetch | null = null;

export const loungeAuthorizedFetch = async (
  input: RequestInfo | URL,
  init?: LoungeFetchInit,
  fetchImpl: typeof fetch = fetch
): Promise<Response> => {
  const url = resolveFetchUrl(input);
  const headers =
    init?.headers ??
    (typeof Request !== "undefined" && input instanceof Request
      ? input.headers
      : undefined);
  const sentBearer = requestSentBearer({ headers });
  const alreadyRetried = Boolean(init?._loungeAuthRetried);
  const method = resolveFetchMethod(input, init);
  const response = await fetchImpl(input as RequestInfo, init);
  if (
    response.status !== 401 ||
    !sentBearer ||
    alreadyRetried ||
    !isLoungeBackendUrl(url)
  ) {
    return response;
  }
  const result = await refreshLoungeAccessToken();
  if (result.kind !== "ok") {
    return response;
  }
  const body =
    init?.body ??
    (typeof Request !== "undefined" && input instanceof Request && !input.bodyUsed
      ? input.body
      : undefined);
  if (
    !isIdempotentMethod(method) ||
    !canReplayRequestBody(body) ||
    !isSafeToReplayUrl(url) ||
    (typeof input !== "string" &&
      !(typeof URL !== "undefined" && input instanceof URL))
  ) {
    return response;
  }
  const retryInit: LoungeFetchInit = {
    ...(init ?? {}),
    headers: writeAuthorization(
      headers,
      formatLoungeBearerAuthorization(result.token)
    ) as HeadersInit,
    _loungeAuthRetried: true,
  };
  return fetchImpl(input as RequestInfo, retryInit);
};

export const installLoungeAuthorizedFetch = () => {
  if (fetchPatched || typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }
  fetchPatched = true;
  nativeFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) =>
    loungeAuthorizedFetch(input, init as LoungeFetchInit, nativeFetch as typeof fetch)
  ) as typeof fetch;
};

export const resetLoungeAuthorizedFetchForTests = () => {
  if (fetchPatched && nativeFetch) {
    window.fetch = nativeFetch;
  }
  fetchPatched = false;
  nativeFetch = null;
};

export const resetLoungeAuthRefreshForTests = () => {
  inFlightRefresh = null;
  lastUnauthorizedToken = null;
  lastLoginCopyAfterRefreshToken = null;
};
